import { useCallback, useEffect, useRef, useState } from 'react';
import { featureApi, phaseApi } from '../../api/endpoints';
import type { Phase } from '../../types';
import type { FeatureWithStatus } from '../../types/acceptance';

/**
 * So sánh để chọn giai đoạn mở sẵn: order_index lớn nhất, hoà thì created_at
 * mới nhất. Theo yêu cầu "mặc định hiện cái có số thứ tự cao nhất và mới nhất".
 */
// Giá trị đánh dấu "chưa từng xử lý effect nào" — không thể trùng với bất kỳ
// projectId thật (string) hay trạng thái "chưa chọn dự án" (null).
const NOT_YET_LOADED = Symbol('usePhaseTree/not-yet-loaded');

export function pickDefaultPhase(phases: Phase[]): Phase | null {
  if (phases.length === 0) return null;
  return [...phases].sort((a, b) => {
    if (b.order_index !== a.order_index) return b.order_index - a.order_index;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  })[0];
}

export interface PhaseTreeState {
  phases: Phase[];
  featuresByPhase: Record<string, FeatureWithStatus[]>;
  loadingPhases: boolean;
  loadingFeatures: Record<string, boolean>;
  phasesError: string | null;
  errorByPhase: Record<string, string | null>;
  /** Tải tính năng của một giai đoạn nếu chưa có trong bộ nhớ đệm. */
  ensurePhaseLoaded: (phaseId: string) => Promise<void>;
  /** Xoá bộ nhớ đệm của một giai đoạn rồi tải lại (sau khi thêm/sửa/xoá). */
  refreshPhase: (phaseId: string) => Promise<void>;
}

/**
 * Nạp cây Giai đoạn → Tính năng theo YÊU CẦU: mở trang chỉ lấy danh sách giai
 * đoạn, tính năng của một giai đoạn chỉ được tải khi người dùng bấm mở nó.
 *
 * Thay cho useAcceptanceTree cũ vốn tải toàn bộ cây một lượt và gọi thêm một
 * request lấy trạng thái nghiệm thu cho MỖI tính năng (hơn 60 request với dự
 * án 63 tính năng). Trạng thái nay đã được backend trả kèm trong danh sách.
 *
 * Lỗi được cô lập theo từng giai đoạn: một giai đoạn hỏng không làm trắng cả
 * cây như hook cũ.
 */
export function usePhaseTree(projectId: string | null): PhaseTreeState {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [featuresByPhase, setFeaturesByPhase] = useState<Record<string, FeatureWithStatus[]>>({});
  const [loadingPhases, setLoadingPhases] = useState(false);
  const [loadingFeatures, setLoadingFeatures] = useState<Record<string, boolean>>({});
  const [phasesError, setPhasesError] = useState<string | null>(null);
  const [errorByPhase, setErrorByPhase] = useState<Record<string, string | null>>({});

  // Bộ nhớ đệm đọc/ghi ĐỒNG BỘ (không qua scheduler của React) để đánh dấu
  // giai đoạn nào đã tải/đang tải. Trước đây dùng "đọc state mới nhất bên
  // trong updater của setState" nhưng cách đó chỉ đúng khi React chạy được
  // "eager bailout" (fiber có lanes === NoLanes); nếu có một setState khác
  // đang chờ xử lý trên cùng component (ví dụ gọi ensurePhaseLoaded cho hai
  // giai đoạn liên tiếp không chờ nhau), updater bị hoãn tới render phase và
  // biến ngoài không được gán kịp — hàm âm thầm return mà không tải gì cả.
  const cacheKeysRef = useRef<Set<string>>(new Set());

  // Lưu projectId đã xử lý ở lần effect trước để phân biệt "đổi dự án thật"
  // với "effect chạy lại vì lý do khác" (ví dụ React 18 StrictMode mount →
  // cleanup → mount lại khi phát triển — cùng projectId, chạy lại effect
  // nhưng không phải đổi dự án). Dùng ref vì cần so sánh đồng bộ ngay trong
  // effect, không qua state/scheduler của React.
  //
  // Khởi tạo bằng NOT_YET_LOADED (không thể trùng projectId thật, kể cả
  // null) để lần effect đầu tiên luôn được coi là "đổi dự án thật" — nếu
  // khởi tạo bằng projectId hiện tại thì lần chạy đầu sẽ bị coi là "không
  // đổi" và bỏ qua cả việc tải danh sách giai đoạn lần đầu.
  const prevProjectIdRef = useRef<string | null | typeof NOT_YET_LOADED>(NOT_YET_LOADED);

  const loadPhases = useCallback(async () => {
    if (!projectId) {
      setPhases([]);
      setFeaturesByPhase({});
      return;
    }
    setLoadingPhases(true);
    setPhasesError(null);
    try {
      const { items } = await phaseApi.listByProject(projectId, { limit: 200 });
      setPhases([...items].sort((a, b) => a.order_index - b.order_index));
    } catch {
      setPhasesError('Không thể tải danh sách giai đoạn.');
      setPhases([]);
    } finally {
      setLoadingPhases(false);
    }
  }, [projectId]);

  useEffect(() => {
    // Effect này chạy lại bất cứ khi nào loadPhases đổi identity (tức là khi
    // projectId đổi), NHƯNG cũng chạy lại do React 18 StrictMode double-invoke
    // lúc dev (mount → cleanup → mount) mà projectId không hề thay đổi. Chỉ
    // xoá bộ nhớ đệm VÀ tải lại danh sách giai đoạn khi projectId THỰC SỰ
    // khác giá trị đã xử lý trước đó — nếu không sẽ:
    //  (a) vô tình xoá cache của giai đoạn vừa tải xong ngay sau khi mở nó;
    //  (b) gọi trùng GET .../phases, kéo dài thời gian trước khi giai đoạn
    //      mặc định được mở — cửa sổ trùng lặp đó càng dài, càng dễ khiến
    //      test đóng/mở lại tưởng nhầm yêu cầu tải lần đầu (đến muộn) là một
    //      lượt tải lại.
    if (prevProjectIdRef.current === projectId) return;
    prevProjectIdRef.current = projectId;
    setFeaturesByPhase({});
    setErrorByPhase({});
    setLoadingFeatures({});
    cacheKeysRef.current = new Set();
    void loadPhases();
  }, [loadPhases]);

  const fetchPhaseFeatures = useCallback(async (phaseId: string) => {
    setLoadingFeatures((prev) => ({ ...prev, [phaseId]: true }));
    setErrorByPhase((prev) => ({ ...prev, [phaseId]: null }));
    try {
      const { items } = await featureApi.listByPhase(phaseId, { limit: 200 });
      setFeaturesByPhase((prev) => ({ ...prev, [phaseId]: items as FeatureWithStatus[] }));
    } catch {
      setErrorByPhase((prev) => ({ ...prev, [phaseId]: 'Không thể tải tính năng của giai đoạn này.' }));
      // Tải lỗi: bỏ đánh dấu để người dùng bấm lại là thử tải lại được, tránh
      // giai đoạn bị "khoá cứng" vĩnh viễn không bao giờ tải được nữa.
      cacheKeysRef.current.delete(phaseId);
    } finally {
      setLoadingFeatures((prev) => ({ ...prev, [phaseId]: false }));
    }
  }, []);

  const ensurePhaseLoaded = useCallback(
    async (phaseId: string) => {
      // Đã có trong bộ nhớ đệm hoặc đang tải dở: không gọi lại. Đọc/ghi
      // cacheKeysRef đồng bộ, KHÔNG phụ thuộc scheduler của React, nên không
      // bị hoãn khi có setState khác đang chờ xử lý (xem ghi chú ở khai báo
      // ref). Đánh dấu TRƯỚC khi fetch để hai lệnh gọi liên tiếp (không chờ
      // nhau) cho cùng phaseId không bắn trùng request.
      if (cacheKeysRef.current.has(phaseId)) return;
      cacheKeysRef.current.add(phaseId);
      await fetchPhaseFeatures(phaseId);
    },
    [fetchPhaseFeatures],
  );

  const refreshPhase = useCallback(
    async (phaseId: string) => {
      cacheKeysRef.current.add(phaseId);
      await fetchPhaseFeatures(phaseId);
    },
    [fetchPhaseFeatures],
  );

  return {
    phases,
    featuresByPhase,
    loadingPhases,
    loadingFeatures,
    phasesError,
    errorByPhase,
    ensurePhaseLoaded,
    refreshPhase,
  };
}
